import { NAVIGATION_DENY_REASONS, normalizeOrigin, parseReturnPath, } from './navigation.js';
import { isProductKey } from './products.js';
/**
 * Account hub routes that other applications send people to (architecture section 5): a product's sign-in
 * page (/login/<product>), the access states and sign-out. The hub registers exactly these paths and
 * destinations build their links with hubUrl, so the two cannot drift apart.
 */
export const HUB_PATHS = {
    login: '/login',
    accessDenied: '/access-denied',
    noWorkspace: '/no-workspace',
    unavailable: '/unavailable',
    logout: '/logout',
    /** 0.20.0: self-serve sign-up (spec 2026-10-18 piece 3); ?returnTo=<hub path> comes back after it. */
    signup: '/signup',
    /** Start your trial (self-serve sign-up, spec 2026-10-18 piece 5), with the pricing page's setup code. */
    start: '/start',
};
/** Query parameter of the hub's sign-in pages that carries the deep link to come back to. */
export const RETURN_TO_PARAM = 'returnTo';
function checkedProduct(productKey) {
    // Typed callers cannot pass anything else, but a product key becomes a path segment here.
    if (productKey !== undefined && !isProductKey(productKey))
        throw new Error('unknown_product');
    return productKey;
}
/** The pricing page's setup code: lowercase letters, digits, dots and commas only (builder.ts encodeSetup). */
const SETUP_CODE = /^[a-z0-9.,]{1,250}$/;
/**
 * An absolute URL on the account hub. A return path passes parseReturnPath and is dropped when refused
 * (the hub then uses the product's registered default path), so a hub link never carries an open
 * redirect or an authorization response.
 */
export function hubUrl(hubOrigin, destination) {
    const origin = normalizeOrigin(hubOrigin);
    const query = new URLSearchParams();
    let path;
    switch (destination.route) {
        case 'login': {
            const productKey = checkedProduct(destination.productKey);
            path = productKey ? `${HUB_PATHS.login}/${productKey}` : HUB_PATHS.login;
            if (destination.returnPath !== undefined) {
                const returnPath = parseReturnPath(destination.returnPath);
                if (returnPath.ok)
                    query.set(RETURN_TO_PARAM, returnPath.path);
            }
            break;
        }
        case 'signup': {
            path = HUB_PATHS.signup;
            if (destination.returnPath !== undefined) {
                const returnPath = parseReturnPath(destination.returnPath);
                if (returnPath.ok)
                    query.set(RETURN_TO_PARAM, returnPath.path);
            }
            break;
        }
        case 'start': {
            path = HUB_PATHS.start;
            // Anything that is not a setup code is dropped: the page then starts from its default setup.
            if (destination.setup !== undefined && SETUP_CODE.test(destination.setup))
                query.set('setup', destination.setup);
            break;
        }
        case 'access-denied': {
            path = HUB_PATHS.accessDenied;
            if (destination.reason !== undefined) {
                if (!NAVIGATION_DENY_REASONS.includes(destination.reason)) {
                    throw new Error('unknown_reason');
                }
                query.set('reason', destination.reason);
            }
            const productKey = checkedProduct(destination.productKey);
            if (productKey)
                query.set('product', productKey);
            break;
        }
        case 'no-workspace':
            path = HUB_PATHS.noWorkspace;
            break;
        case 'unavailable':
        case 'logout': {
            path = destination.route === 'unavailable' ? HUB_PATHS.unavailable : HUB_PATHS.logout;
            const productKey = checkedProduct(destination.productKey);
            if (productKey)
                query.set('product', productKey);
            break;
        }
    }
    const search = query.toString();
    return `${origin}${path}${search ? `?${search}` : ''}`;
}
/**
 * The query parameter in which a redirect from the hub hands an Auth0 destination the organization the person
 * chose (an employee context's organizationHint), so the destination's own sign-in can send it: products
 * require an Auth0 organization at sign-in and could not know it otherwise. platform-api adds it to redirect
 * decisions for Auth0 sessions into an employee context with an Auth0 organization, and removes any a return path
 * carried from every other redirect; DestinationGate takes it out of its address once per page, before anything
 * else reads the address, and sends it only on the way back from its own hub trip. It is a sign-in hint, never
 * an authorization: Auth0 signs in only members of that organization, and the product's access check still
 * decides.
 */
export const SIGN_IN_ORGANIZATION_PARAM = 'trace_organization';
const ORGANIZATION_REFERENCE = /^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$/;
/** True for an Auth0 organization id or name (letters, digits, underscore and hyphen, at most 64). */
export function isOrganizationReference(value) {
    return typeof value === 'string' && ORGANIZATION_REFERENCE.test(value);
}
/** Splits a URL or a path into what comes before the query, its query parameters as written, and the fragment. */
function splitQuery(address) {
    const hashAt = address.indexOf('#');
    const beforeHash = hashAt === -1 ? address : address.slice(0, hashAt);
    const hash = hashAt === -1 ? '' : address.slice(hashAt);
    const queryAt = beforeHash.indexOf('?');
    if (queryAt === -1)
        return { base: beforeHash, params: [], hash };
    const params = beforeHash
        .slice(queryAt + 1)
        .split('&')
        .filter((param) => param !== '');
    return { base: beforeHash.slice(0, queryAt), params, hash };
}
const isSignInOrganizationParam = (param) => param === SIGN_IN_ORGANIZATION_PARAM || param.startsWith(`${SIGN_IN_ORGANIZATION_PARAM}=`);
function joinQuery(base, params, hash) {
    return `${base}${params.length > 0 ? `?${params.join('&')}` : ''}${hash}`;
}
/**
 * The destination URL with the organization to sign in to, after its query and before its fragment. Any
 * occurrence the URL already carried (a return path a caller wrote) is replaced; every other parameter is kept
 * exactly as written. Throws for a value that is not an organization reference.
 */
export function withSignInOrganization(url, organization) {
    if (!isOrganizationReference(organization))
        throw new Error('invalid_organization_reference');
    const { base, params, hash } = splitQuery(url);
    const kept = params.filter((param) => !isSignInOrganizationParam(param));
    return joinQuery(base, [...kept, `${SIGN_IN_ORGANIZATION_PARAM}=${organization}`], hash);
}
/**
 * Takes the organization a redirect handed over out of an address (a path with its query and fragment, or a
 * URL): the address without any occurrence of the parameter, and the organization when it held exactly one
 * well-formed reference (null otherwise: repeated, empty or malformed values are dropped, never guessed at).
 */
export function takeSignInOrganization(address) {
    const { base, params, hash } = splitQuery(address);
    const values = params.filter(isSignInOrganizationParam).map((param) => {
        const raw = param.slice(SIGN_IN_ORGANIZATION_PARAM.length + 1);
        try {
            return decodeURIComponent(raw);
        }
        catch {
            return null;
        }
    });
    const kept = params.filter((param) => !isSignInOrganizationParam(param));
    const [only] = values;
    return {
        organization: values.length === 1 && isOrganizationReference(only) ? only : null,
        path: joinQuery(base, kept, hash),
    };
}
//# sourceMappingURL=hub.js.map